import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { Box, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import HTMLFlipBook from "react-pageflip";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import AudioManager from "../../@shared/utils/AudioManager";
import DialogHUD from "./DialogHUD";

const getPanelKey = (pageId, frameId) => `${pageId}:${frameId}`;
const getFrameThumbnail = (frame = {}) => frame.thumbnail || frame.image;
const parsePanelArea = (area = "") =>
  String(area)
    .split("/")
    .map((part) => Number(part.trim()));

const sortPanelsByReadingOrder = (panels = []) =>
  [...panels].sort((leftPanel, rightPanel) => {
    if (leftPanel.pageIndex !== rightPanel.pageIndex) {
      return leftPanel.pageIndex - rightPanel.pageIndex;
    }

    const [leftRowStart = 0, leftColumnStart = 0, leftRowEnd = 0, leftColumnEnd = 0] =
      parsePanelArea(leftPanel.area);
    const [rightRowStart = 0, rightColumnStart = 0, rightRowEnd = 0, rightColumnEnd = 0] =
      parsePanelArea(rightPanel.area);

    if (leftRowStart !== rightRowStart) return leftRowStart - rightRowStart;
    if (leftColumnStart !== rightColumnStart) return leftColumnStart - rightColumnStart;
    if (leftRowEnd !== rightRowEnd) return leftRowEnd - rightRowEnd;
    if (leftColumnEnd !== rightColumnEnd) return leftColumnEnd - rightColumnEnd;

    return leftPanel.frameIndex - rightPanel.frameIndex;
  });

const BLANK_FILLER_PAGE_ID = "__blank-filler-page__";
const CUT_SCENE_INTRO_DELAY_MS = 2000;
const CUT_SCENE_INTRO_DURATION_MS = 2000;
const CUT_SCENE_INTRO_LOCK_MS =
  CUT_SCENE_INTRO_DELAY_MS + CUT_SCENE_INTRO_DURATION_MS;
const CUT_SCENE_AUTO_ADVANCE_DELAY_MS = 300;
const CUT_SCENE_NEXT_PANEL_OPEN_DELAY_MS = 750;
const CUT_SCENE_PAGE_TURN_DURATION_MS = 900;
const CUT_SCENE_INTRO_EASE = [0.2, 0.9, 0.28, 1];
const CUT_SCENE_BOOK_INTRO_VARIANTS = {
  hidden: {
    opacity: 0,
    y: 84,
    z: 36,
    rotateX: 16,
    scale: 0.972,
  },
  visible: {
    opacity: 1,
    y: 0,
    z: 0,
    rotateX: 0,
    scale: 1,
  },
};
const CUT_SCENE_BOOK_REDUCED_VARIANTS = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
  },
};
const CUT_SCENE_SHADOW_VARIANTS = {
  hidden: {
    opacity: 0.08,
    scaleX: 0.68,
    scaleY: 0.52,
    y: -22,
  },
  visible: {
    opacity: 0.46,
    scaleX: 1,
    scaleY: 1,
    y: 0,
  },
};
const CUT_SCENE_SHADOW_REDUCED_VARIANTS = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 0.42,
  },
};

const getFrameScript = (frame = {}) => {
  if (Array.isArray(frame.script) && frame.script.length > 0) {
    return frame.script.map((beat, index) => ({
      id: beat.id || `${frame.id}-beat-${index + 1}`,
      text: beat.text || "",
      speaker: beat.speaker || null,
    }));
  }

  if (frame.text) {
    return [
      {
        id: `${frame.id}-beat-1`,
        text: frame.text,
        speaker: frame.speaker || null,
      },
    ];
  }

  return [];
};

const DEFAULT_OVERLAY_BOX = {
  enabled: true,
  theme: "light",
  widthPercent: 70,
  topPercent: 6,
  leftPercent: 6,
  fontSizeCqw: 4.4,
  rotationDeg: 0,
};

const clampOverlayValue = (value, fallback, min, max) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
};

const getLegacyTextBoxStyle = (frame = {}, isVideo = false) => {
  if (frame?.textBoxStyle) {
    return frame.textBoxStyle;
  }

  return isVideo ? "act1-narration" : "bottom-caption-dark";
};

const getLegacyOverlayBox = (style = "bottom-caption-dark") => {
  switch (style) {
    case "act1-narration":
      return {
        enabled: true,
        theme: "light",
        widthPercent: 52,
        topPercent: 6,
        leftPercent: 6,
        fontSizeCqw: 4.4,
        rotationDeg: 0,
      };
    case "top-caption-dark":
      return {
        enabled: true,
        theme: "dark",
        widthPercent: 92,
        topPercent: 6,
        leftPercent: 4,
        fontSizeCqw: 3.4,
        rotationDeg: 0,
      };
    case "bottom-caption-light":
      return {
        enabled: true,
        theme: "light",
        widthPercent: 92,
        topPercent: 72,
        leftPercent: 4,
        fontSizeCqw: 3.4,
        rotationDeg: 0,
      };
    case "bottom-caption-dark":
      return {
        enabled: true,
        theme: "dark",
        widthPercent: 94,
        topPercent: 78,
        leftPercent: 3,
        fontSizeCqw: 3.2,
        rotationDeg: 0,
      };
    case "none":
    default:
      return {
        ...DEFAULT_OVERLAY_BOX,
        enabled: false,
      };
  }
};

const getOverlayBoxConfig = (frame = {}, isVideo = false) => {
  if (frame?.overlayBox) {
    return {
      enabled:
        typeof frame.overlayBox.enabled === "boolean"
          ? frame.overlayBox.enabled
          : DEFAULT_OVERLAY_BOX.enabled,
      theme:
        frame.overlayBox.theme === "dark" || frame.overlayBox.theme === "light"
          ? frame.overlayBox.theme
          : DEFAULT_OVERLAY_BOX.theme,
      widthPercent: clampOverlayValue(
        frame.overlayBox.widthPercent,
        DEFAULT_OVERLAY_BOX.widthPercent,
        10,
        100,
      ),
      topPercent: clampOverlayValue(
        frame.overlayBox.topPercent,
        DEFAULT_OVERLAY_BOX.topPercent,
        -100,
        100,
      ),
      leftPercent: clampOverlayValue(
        frame.overlayBox.leftPercent,
        DEFAULT_OVERLAY_BOX.leftPercent,
        -100,
        100,
      ),
      fontSizeCqw: clampOverlayValue(
        frame.overlayBox.fontSizeCqw,
        DEFAULT_OVERLAY_BOX.fontSizeCqw,
        0.5,
        20,
      ),
      rotationDeg: clampOverlayValue(
        frame.overlayBox.rotationDeg,
        DEFAULT_OVERLAY_BOX.rotationDeg,
        -180,
        180,
      ),
    };
  }

  return getLegacyOverlayBox(getLegacyTextBoxStyle(frame, isVideo));
};

const getFrameOverlayText = (frame = {}, isVideo = false) => {
  if (typeof frame.overlayText === "string" && frame.overlayText.trim()) {
    return frame.overlayText.trim();
  }

  if (typeof frame.text === "string" && frame.text.trim()) {
    return frame.text.trim();
  }

  if (isVideo) {
    return getFrameScript(frame)[0]?.text || "";
  }

  return "";
};

const getExpandedScript = (frame = {}, isVideo = false) => {
  if (isVideo || frame?.hideExpandedText) return [];
  return getFrameScript(frame);
};

const getOverlayBoxSx = (overlayBox = DEFAULT_OVERLAY_BOX) => ({
  left: `${overlayBox.leftPercent}%`,
  top: `${overlayBox.topPercent}%`,
  width: `${overlayBox.widthPercent}%`,
  transform: `rotate(${overlayBox.rotationDeg}deg)`,
  transformOrigin: "top left",
  bgcolor:
    overlayBox.theme === "dark" ? "rgba(0,0,0,0.84)" : "rgba(255,255,255,0.94)",
  border:
    overlayBox.theme === "dark"
      ? "1px solid rgba(255,255,255,0.26)"
      : "2px solid rgba(15,23,42,0.82)",
  borderRadius: 1.1,
  p: 0.7,
  boxShadow:
    overlayBox.theme === "dark"
      ? "0 10px 24px rgba(2,6,23,0.28)"
      : "0 10px 24px rgba(2,6,23,0.22)",
});

const getOverlayTextSx = (overlayBox = DEFAULT_OVERLAY_BOX) => ({
  fontSize: `${overlayBox.fontSizeCqw}cqw`,
  lineHeight: 1.25,
  color: overlayBox.theme === "dark" ? "#f8fafc" : "#020617",
  fontWeight: overlayBox.theme === "dark" ? 700 : 800,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
});

const buildPanelSelection = (page, pageIndex, frame, frameIndex) => {
  const panelKey = getPanelKey(page?.id, frame.id);

  return {
    ...frame,
    layoutId: panelKey,
    panelKey,
    pageId: page?.id,
    pageTitle: page?.title,
    pageIndex,
    frameIndex,
    script: getFrameScript(frame),
  };
};

const getSpreadIndices = (pageCount, orientation, showCover = false) => {
  if (pageCount <= 0) return [];

  if (orientation !== "landscape") {
    return Array.from({ length: pageCount }, (_, pageIndex) => [pageIndex]);
  }

  const spreads = [];
  let nextPageIndex = 0;

  if (showCover) {
    spreads.push([0]);
    nextPageIndex = 1;
  }

  for (let pageIndex = nextPageIndex; pageIndex < pageCount; pageIndex += 2) {
    if (pageIndex < pageCount - 1) {
      spreads.push([pageIndex, pageIndex + 1]);
    } else {
      spreads.push([pageIndex]);
    }
  }

  return spreads;
};

function Frame({
  layoutId,
  area,
  image,
  thumbnail,
  alt,
  text,
  onClick,
  showPanelText,
  isLocked,
  isCompleted,
  isCurrent,
  isElevated,
  isVideo,
  overlayBox,
}) {
  const resolvedOverlayBox = getOverlayBoxConfig(overlayBox, isVideo);
  const hasPanelText = Boolean(
    (showPanelText || isVideo) && text && resolvedOverlayBox.enabled,
  );
  const handleClick = useCallback(
    (event) => {
      if (!onClick) return;

      event.stopPropagation();
      onClick();
    },
    [onClick],
  );

  return (
    <Box
      component={motion.button}
      layoutId={layoutId}
      type="button"
      onClick={handleClick}
      disabled={Boolean(isLocked || !onClick)}
      aria-label={alt || layoutId}
      sx={{
        gridArea: area,
        border: "3px solid #ffffff",
        borderRadius: 1.5,
        boxSizing: "border-box",
        overflow: "visible",
        bgcolor: "#111",
        position: "relative",
        zIndex: isElevated ? 1000 : "auto",
        cursor: onClick ? "pointer" : "default",
        padding: 0,
        appearance: "none",
        containerType: hasPanelText ? "inline-size" : "normal",
        width: "100%",
        height: "100%",
        opacity: isLocked ? 0.92 : 1,
        boxShadow: isCurrent
          ? "0 0 0 2px rgba(59, 130, 246, 0.28)"
          : isCompleted
            ? "0 0 0 1px rgba(34,197,94,0.24)"
            : "none",
        "&:disabled": {
          cursor: "default",
        },
      }}
      whileHover={{ scale: onClick ? 1.01 : 1 }}
      whileTap={{ scale: onClick ? 0.99 : 1 }}
    >
      <img
        src={thumbnail || image}
        alt={alt || "frame"}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: "inherit",
        }}
      />
      {hasPanelText ? (
        <Box
          data-testid={isVideo ? `video-panel-text-${layoutId}` : `panel-text-${layoutId}`}
          sx={{
            position: "absolute",
            textAlign: "left",
            pointerEvents: "none",
            zIndex: 1,
            ...getOverlayBoxSx(resolvedOverlayBox),
          }}
        >
          <Box
            component="span"
            sx={getOverlayTextSx(resolvedOverlayBox)}
          >
            {text}
          </Box>
        </Box>
      ) : null}

      {isVideo ? (
        <Box
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(8,12,28,0.72)",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "#f8fafc",
            pointerEvents: "none",
          }}
        >
          <PlayArrowRoundedIcon />
        </Box>
      ) : null}

      {isLocked ? (
        <Box
          data-testid={`panel-lock-${layoutId}`}
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(7, 12, 26, 0.42)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.12)",
            pointerEvents: "none",
          }}
        />
      ) : null}
    </Box>
  );
}

const ComicPage = forwardRef(function ComicPage(
  {
    page,
    pageIndex,
    allowZoom,
    onSelectFrame,
    onSelectPanel,
    onSelectPage,
    showPageMeta,
    showPanelText,
    enforcePanelSequence,
    currentPanelKey,
    elevatedPanelKey,
    completedPanels,
    isContinuePageLocked,
  },
  ref,
) {
  if (page?.kind === "cover") {
    return (
      <div
        ref={ref}
        data-density="hard"
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            position: "relative",
            overflow: "hidden",
            bgcolor: "#050816",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
          }}
        >
          <img
            src={page.image}
            alt={page.alt || "cover"}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
      </div>
    );
  }

  if (page?.kind === "continue") {
    return (
      <div
        ref={ref}
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <ContinuePage page={page} isLocked={isContinuePageLocked} />
      </div>
    );
  }

  if (page?.kind === "blank") {
    return (
      <div
        ref={ref}
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box
          role="img"
          aria-label={page.alt || "Blank page"}
          data-testid="blank-filler-page"
          sx={{
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
            bgcolor: "#ffffff",
            boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.08)",
          }}
        />
      </div>
    );
  }

  const hasTitle = Boolean(page?.title);
  const shouldShowPageHeader = showPageMeta && hasTitle;

  return (
    <div
      ref={ref}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        onClick={onSelectPage ? () => onSelectPage(page, pageIndex) : undefined}
        sx={{
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
          bgcolor: "#fff8ea",
          color: "#0f172a",
          p: 1.5,
          display: "grid",
          gridTemplateRows: shouldShowPageHeader ? "auto 1fr" : "1fr",
          gap: 1.5,
          boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.08)",
          backgroundImage:
            "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(252,248,238,0.98) 100%)",
          cursor: onSelectPage ? "pointer" : "default",
        }}
      >
        {shouldShowPageHeader ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "2px solid rgba(15,23,42,0.12)",
              pb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 16, md: 18 },
                fontWeight: 900,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {page.title}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.1em",
                opacity: 0.6,
              }}
            >
              {page.pageLabel || ""}
            </Typography>
          </Box>
        ) : null}

        <Box
          sx={{
            minHeight: 0,
            display: "grid",
            gridTemplateRows: "repeat(12, 1fr)",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 1.5,
          }}
        >
          {page.frames.map((frame, frameIndex) => {
            const layoutId = getPanelKey(page.id, frame.id);
            const panelSelection = buildPanelSelection(
              page,
              pageIndex,
              frame,
              frameIndex,
            );
            const isCompleted = Boolean(completedPanels[layoutId]);
            const isCurrent = currentPanelKey === layoutId;
            const isElevated = elevatedPanelKey === layoutId;
            const isVideo = Boolean(frame.videoUrl);
            const panelText = getFrameOverlayText(frame, isVideo);
            const isLocked =
              enforcePanelSequence && !isCompleted && !isCurrent;
            const canOpen =
              allowZoom && (!enforcePanelSequence || isCurrent || isCompleted);
            const canSelectPanel =
              !isLocked && Boolean(onSelectPanel) && !page?.kind;

            return (
              <Frame
                key={layoutId}
                layoutId={layoutId}
                area={frame.area}
                image={frame.image}
                thumbnail={getFrameThumbnail(frame)}
                alt={frame.alt}
                text={panelText}
                overlayBox={frame}
                showPanelText={showPanelText}
                isLocked={isLocked}
                isCompleted={isCompleted}
                isCurrent={isCurrent}
                isElevated={isElevated}
                isVideo={isVideo}
                onClick={
                  canOpen
                    ? () => onSelectFrame({ ...panelSelection })
                    : canSelectPanel
                      ? () => onSelectPanel({ ...panelSelection })
                    : undefined
                }
              />
            );
          })}
        </Box>
      </Box>
    </div>
  );
});

const getVisiblePageIndices = (page, orientation, pageCount, showCover = false) => {
  const spreads = getSpreadIndices(pageCount, orientation, showCover);
  const activeSpread = spreads.find((spread) => spread.includes(page));

  return activeSpread || [];
};

const getLastStartIndex = (pageCount, orientation, showCover = false) => {
  const spreads = getSpreadIndices(pageCount, orientation, showCover);

  return spreads.length > 0 ? spreads[spreads.length - 1][0] : 0;
};

const clampPageIndex = (page, pageCount, orientation, showCover = false) => {
  if (pageCount <= 0) return 0;

  const clampedPage = Math.max(0, Math.min(page, pageCount - 1));
  const spreads = getSpreadIndices(pageCount, orientation, showCover);
  const activeSpread = spreads.find((spread) => spread.includes(clampedPage));

  return activeSpread ? activeSpread[0] : 0;
};

const getViewportSize = () => {
  if (typeof window === "undefined") {
    return { width: 1280, height: 900 };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
};

function ContinuePage({ page, isLocked = false }) {
  return (
    <Box
      role="img"
      aria-label={page?.label || "Continue page"}
      sx={{
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        maxHeight: "100%",
        boxSizing: "border-box",
        border: "1px solid rgba(15,23,42,0.12)",
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
        display: "grid",
        placeItems: "center",
        background: "#ffffff",
        color: "#0f172a",
        cursor: "default",
        boxShadow:
          "0 30px 60px rgba(0,0,0,0.42), inset 0 0 0 1px rgba(255,255,255,0.06)",
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", px: 3 }}>
        <Typography
          sx={{
            fontSize: { xs: 28, md: 40 },
            fontWeight: 900,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {page?.label || "Continue..."}
        </Typography>

        {page?.prompt ? (
          <Typography
            sx={{
              mt: 1.5,
              fontSize: { xs: 14, md: 16 },
              color: "rgba(15,23,42,0.72)",
            }}
          >
            {page.prompt}
          </Typography>
        ) : null}
      </Box>

      {isLocked ? (
        <Box
          data-testid="continue-page-lock"
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(255,255,255,0.6)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(15,23,42,0.12)",
            px: 3,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: "rgba(15,23,42,0.86)",
              fontSize: { xs: 13, md: 15 },
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Finish the unlocked panels to continue.
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

export default function CutSceneFlipBook({
  pages = [],
  cover = null,
  initialPage = 0,
  initialCompletedPanels = null,
  onPageChange,
  onProgressChange,
  onPageSelect,
  onPanelSelect,
  allowZoom = true,
  showNav = true,
  showPageMeta = true,
  showPanelText = true,
  showStatus = true,
  enforcePanelSequence = true,
  pauseBackgroundMusic = true,
  onPanelComplete,
  onSpreadComplete,
  continuePage = null,
  onCutSceneComplete,
  viewportSizeOverride = null,
  autoAdvanceFirstSpread = false,
  disableIntroAnimation = false,
}) {
  const theme = useTheme();
  const shouldReduceMotion = useReducedMotion();
  const shouldSkipIntro = shouldReduceMotion || disableIntroAnimation;
  const hasCover = Boolean(cover?.image);
  const hasContinuePage = Boolean(continuePage);
  const initialBookPage = Math.max(0, initialPage);
  const initialCompletedPanelMap = useMemo(
    () =>
      Array.isArray(initialCompletedPanels)
        ? initialCompletedPanels.reduce((acc, panelKey) => {
            if (panelKey) acc[panelKey] = true;
            return acc;
          }, {})
        : null,
    [initialCompletedPanels],
  );
  const flipBookRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(initialBookPage);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [elevatedPanelKey, setElevatedPanelKey] = useState(null);
  const [completedPanels, setCompletedPanels] = useState(initialCompletedPanelMap || {});
  const [isCompletingCutScene, setIsCompletingCutScene] = useState(false);
  const [isIntroPlaying, setIsIntroPlaying] = useState(() => !shouldSkipIntro);
  const [viewportSize, setViewportSize] = useState(
    () => viewportSizeOverride || getViewportSize(),
  );
  const [orientation, setOrientation] = useState(
    viewportSize.width >= 900 ? "landscape" : "portrait",
  );
  const isLandscapeBook = orientation === "landscape";
  const authoredPageCount = pages.length;
  const shouldInsertCoverBlankPage =
    hasCover && isLandscapeBook && authoredPageCount > 0;
  const shouldPrependBlankPage =
    !hasCover && isLandscapeBook && authoredPageCount > 0 && authoredPageCount % 2 === 1;
  const shouldAppendContinuePage =
    hasContinuePage &&
    authoredPageCount > 0 &&
    (isLandscapeBook ? authoredPageCount % 2 === 0 : true);
  const bookPages = useMemo(() => {
    const composedPages = [];

    if (hasCover) {
      composedPages.push({
        id: cover.id || "cover-page",
        kind: "cover",
        image: cover.image,
        alt: cover.alt || "cover",
        frames: [],
      });
    }

    if (shouldInsertCoverBlankPage || shouldPrependBlankPage) {
      composedPages.push({
        id: BLANK_FILLER_PAGE_ID,
        kind: "blank",
        alt: "Blank page",
        frames: [],
      });
    }

    composedPages.push(...pages);

    if (shouldAppendContinuePage) {
      composedPages.push({
        id: continuePage.id || "continue-page",
        kind: "continue",
        ...continuePage,
        frames: [],
      });
    }

    return composedPages;
  }, [
    continuePage,
    cover,
    hasCover,
    pages,
    shouldAppendContinuePage,
    shouldInsertCoverBlankPage,
    shouldPrependBlankPage,
  ]);
  const isDesktop = viewportSize.width >= 900;
  const reservedHorizontalSpace = isDesktop ? 112 : 28;
  const reservedVerticalSpace = isDesktop ? 72 : 72;
  const targetVisiblePages = isDesktop ? 2 : 1;
  const maxPageWidth = isDesktop ? 760 : 420;
  const minPageWidth = isDesktop ? 260 : 240;
  const availableWidth = Math.max(
    minPageWidth,
    viewportSize.width - reservedHorizontalSpace,
  );
  const availableHeight = Math.max(
    Math.round(minPageWidth * (7 / 5)),
    viewportSize.height - reservedVerticalSpace,
  );
  const computedPageWidth = Math.floor(
    Math.min(
      availableWidth / targetVisiblePages,
      availableHeight * (5 / 7),
      maxPageWidth,
    ),
  );
  const pageWidth = Math.max(minPageWidth, computedPageWidth);
  const pageHeight = Math.round(pageWidth * (7 / 5));
  const visiblePageCount = orientation === "landscape" ? 2 : 1;
  const bookWidth = pageWidth * visiblePageCount;
  const bookHeight = pageHeight;
  const pageCount = bookPages.length;
  const usesLibraryCover = hasCover;
  const lastBookPageIndex = useMemo(
    () => getLastStartIndex(pageCount, orientation, usesLibraryCover),
    [orientation, pageCount, usesLibraryCover],
  );
  const visiblePageIndices = useMemo(
    () =>
      getVisiblePageIndices(
        currentPage,
        orientation,
        pageCount,
        usesLibraryCover,
      ),
    [currentPage, orientation, pageCount, usesLibraryCover],
  );
  const visiblePanels = useMemo(
    () =>
      visiblePageIndices.flatMap((pageIndex) =>
        sortPanelsByReadingOrder((bookPages[pageIndex]?.frames || []).map((frame, frameIndex) =>
          buildPanelSelection(
            bookPages[pageIndex],
            pageIndex,
            frame,
            frameIndex,
          ),
        )),
      ),
    [bookPages, visiblePageIndices],
  );
  const isContinuePageInView = useMemo(
    () =>
      visiblePageIndices.some(
        (pageIndex) => bookPages[pageIndex]?.kind === "continue",
      ),
    [bookPages, visiblePageIndices],
  );
  const currentPanelKey = useMemo(() => {
    if (!enforcePanelSequence) return null;

    return (
      visiblePanels.find((panel) => !completedPanels[panel.panelKey])?.panelKey ||
      null
    );
  }, [completedPanels, enforcePanelSequence, visiblePanels]);
  const isCurrentScopeComplete = !enforcePanelSequence || currentPanelKey === null;
  const canTurnPages =
    !isIntroPlaying &&
    !selectedPanel &&
    !isCompletingCutScene &&
    (!enforcePanelSequence || isCurrentScopeComplete);
  const canCompleteCutScene =
    !selectedPanel &&
    !isCompletingCutScene &&
    authoredPageCount > 0 &&
    hasContinuePage &&
    isCurrentScopeComplete &&
    ((shouldAppendContinuePage && isContinuePageInView) ||
      (!shouldAppendContinuePage && currentPage >= lastBookPageIndex));
  const canGoBack =
    !isIntroPlaying &&
    !selectedPanel &&
    !isCompletingCutScene &&
    currentPage > 0;
  const completedSpreadKeysRef = useRef(new Set());
  const completionTimeoutRef = useRef(null);
  const introTimeoutRef = useRef(null);
  const autoProgressTimeoutRef = useRef(null);
  const autoProgressTokenRef = useRef(0);
  const pendingAutoTurnTargetRef = useRef(null);
  const autoFlipTargetPageRef = useRef(null);
  const didScheduleCoverOpenRef = useRef(false);
  const didScheduleFirstSpreadAdvanceRef = useRef(false);
  const latestStateRef = useRef(null);
  const bookIntroVariants = shouldSkipIntro
    ? CUT_SCENE_BOOK_REDUCED_VARIANTS
    : CUT_SCENE_BOOK_INTRO_VARIANTS;
  const shadowIntroVariants = shouldSkipIntro
    ? CUT_SCENE_SHADOW_REDUCED_VARIANTS
    : CUT_SCENE_SHADOW_VARIANTS;
  const introTransition = shouldSkipIntro
    ? { duration: 0.12, ease: "linear" }
    : {
        delay: CUT_SCENE_INTRO_DELAY_MS / 1000,
        duration: CUT_SCENE_INTRO_DURATION_MS / 1000,
        ease: CUT_SCENE_INTRO_EASE,
      };
  latestStateRef.current = {
    bookPages,
    canCompleteCutScene,
    completedPanels,
    currentPage,
    currentPanelKey,
    isCompletingCutScene,
    isIntroPlaying,
    orientation,
    pageCount,
    selectedPanel,
    usesLibraryCover,
    visiblePageIndices,
    visiblePanels,
  };

  const visiblePageLabel = useMemo(() => {
    if (
      visiblePageIndices.length === 1 &&
      bookPages[visiblePageIndices[0]]?.kind === "cover"
    ) {
      return "Cover";
    }
    if (pageCount <= 0) return "Page 0 of 0";
    if (orientation !== "landscape") return `Page ${currentPage + 1} of ${pageCount}`;

    const endPage = Math.min(currentPage + 2, pageCount);
    return currentPage + 1 === endPage
      ? `Page ${currentPage + 1} of ${pageCount}`
      : `Pages ${currentPage + 1}-${endPage} of ${pageCount}`;
  }, [
    currentPage,
    bookPages,
    orientation,
    pageCount,
    visiblePageIndices,
  ]);

  const syncCurrentPage = useCallback(
    (nextPage) => {
      setCurrentPage(nextPage);
      onPageChange?.(nextPage);
    },
    [onPageChange],
  );

  const cancelPendingAutoProgression = useCallback(
    ({ clearTurnTarget = true } = {}) => {
      if (autoProgressTimeoutRef.current) {
        window.clearTimeout(autoProgressTimeoutRef.current);
        autoProgressTimeoutRef.current = null;
      }

      if (clearTurnTarget) {
        pendingAutoTurnTargetRef.current = null;
        autoFlipTargetPageRef.current = null;
      }

      autoProgressTokenRef.current += 1;
    },
    [],
  );

  const scheduleAutoProgression = useCallback(
    (callback, delay = CUT_SCENE_AUTO_ADVANCE_DELAY_MS) => {
      if (autoProgressTimeoutRef.current) {
        window.clearTimeout(autoProgressTimeoutRef.current);
      }

      const nextToken = autoProgressTokenRef.current + 1;
      autoProgressTokenRef.current = nextToken;
      autoProgressTimeoutRef.current = window.setTimeout(() => {
        if (autoProgressTokenRef.current !== nextToken) {
          return;
        }

        autoProgressTimeoutRef.current = null;
        callback();
      }, delay);
    },
    [],
  );

  const getPanelsForPageIndices = useCallback((pageIndices) => {
    const { bookPages: latestBookPages = [] } = latestStateRef.current || {};

    return pageIndices.flatMap((pageIndex) =>
      sortPanelsByReadingOrder((latestBookPages[pageIndex]?.frames || []).map((frame, frameIndex) =>
        buildPanelSelection(
          latestBookPages[pageIndex],
          pageIndex,
          frame,
          frameIndex,
        ),
      )),
    );
  }, []);

  const getFirstIncompletePanel = useCallback(
    (pageIndices) => {
      const { completedPanels: latestCompletedPanels = {} } =
        latestStateRef.current || {};

      return (
        getPanelsForPageIndices(pageIndices).find(
          (panel) => !latestCompletedPanels[panel.panelKey],
        ) || null
      );
    },
    [getPanelsForPageIndices],
  );

  const getNextSpreadStartPage = useCallback(() => {
    const {
      currentPage: latestCurrentPage = 0,
      orientation: latestOrientation = "portrait",
      pageCount: latestPageCount = 0,
      usesLibraryCover: latestUsesLibraryCover = false,
    } = latestStateRef.current || {};
    const spreads = getSpreadIndices(
      latestPageCount,
      latestOrientation,
      latestUsesLibraryCover,
    );
    const currentSpreadIndex = spreads.findIndex((spread) =>
      spread.includes(latestCurrentPage),
    );

    return currentSpreadIndex >= 0
      ? (spreads[currentSpreadIndex + 1]?.[0] ?? null)
      : null;
  }, []);

  const completePanel = useCallback(
    (panel, reason) => {
      if (!panel?.panelKey) return false;

      let didComplete = false;
      setCompletedPanels((current) => {
        if (current[panel.panelKey]) {
          return current;
        }

        didComplete = true;
        return {
          ...current,
          [panel.panelKey]: true,
        };
      });

      if (didComplete) {
        onPanelComplete?.(panel.panelKey, {
          pageId: panel.pageId,
          pageTitle: panel.pageTitle,
          frameId: panel.id,
          pageIndex: panel.pageIndex,
          reason,
        });
      }

      return didComplete;
    },
    [onPanelComplete],
  );

  const openPanelViewer = useCallback((panel) => {
    if (!panel?.panelKey) return;

    flushSync(() => {
      setElevatedPanelKey(panel.panelKey);
    });
    setSelectedPanel(panel);
  }, []);

  const scheduleNextAutoProgression = useCallback(() => {
    scheduleAutoProgression(() => {
      const {
        isCompletingCutScene: latestIsCompletingCutScene,
        isIntroPlaying: latestIsIntroPlaying,
        selectedPanel: latestSelectedPanel,
        visiblePageIndices: latestVisiblePageIndices = [],
        orientation: latestOrientation = "portrait",
        pageCount: latestPageCount = 0,
        usesLibraryCover: latestUsesLibraryCover = false,
      } = latestStateRef.current || {};

      if (
        latestSelectedPanel ||
        latestIsIntroPlaying ||
        latestIsCompletingCutScene
      ) {
        return;
      }

      const nextVisiblePanel = getFirstIncompletePanel(latestVisiblePageIndices);
      if (nextVisiblePanel) {
        openPanelViewer(nextVisiblePanel);
        return;
      }

      const nextSpreadStartPage = getNextSpreadStartPage();
      if (nextSpreadStartPage === null) {
        return;
      }

      const nextSpreadPageIndices = getVisiblePageIndices(
        nextSpreadStartPage,
        latestOrientation,
        latestPageCount,
        latestUsesLibraryCover,
      );
      const nextSpreadPanel = getFirstIncompletePanel(nextSpreadPageIndices);
      pendingAutoTurnTargetRef.current = {
        page: nextSpreadStartPage,
        shouldOpenPanel: Boolean(nextSpreadPanel),
      };
      autoFlipTargetPageRef.current = nextSpreadStartPage;
      flipBookRef.current?.pageFlip()?.flipNext("top");
    }, CUT_SCENE_NEXT_PANEL_OPEN_DELAY_MS);
  }, [
    getFirstIncompletePanel,
    getNextSpreadStartPage,
    openPanelViewer,
    scheduleAutoProgression,
  ]);

  const closePanelViewer = useCallback(
    ({ markComplete = false, reason = "close" } = {}) => {
      if (!selectedPanel) return;

      const didComplete =
        markComplete && !completedPanels[selectedPanel.panelKey]
          ? completePanel(selectedPanel, reason)
          : false;

      setSelectedPanel(null);
      if (didComplete) {
        scheduleNextAutoProgression();
      }
    },
    [
      completePanel,
      completedPanels,
      scheduleNextAutoProgression,
      selectedPanel,
    ],
  );

  const handleSelectPanel = useCallback(
    (panel) => {
      if (isIntroPlaying) return;
      cancelPendingAutoProgression();
      openPanelViewer(panel);
    },
    [cancelPendingAutoProgression, isIntroPlaying, openPanelViewer],
  );

  const finishSelectedPanel = useCallback(() => {
    if (!selectedPanel) return;

    closePanelViewer({ markComplete: true, reason: "continue" });
  }, [closePanelViewer, selectedPanel]);

  const handleFlip = useCallback(
    (event) => {
      const nextPage = event.data;
      if (
        nextPage !== currentPage &&
        autoFlipTargetPageRef.current !== nextPage
      ) {
        cancelPendingAutoProgression();
      }

      if (!canTurnPages && nextPage !== currentPage) {
        flipBookRef.current?.pageFlip?.()?.turnToPage?.(currentPage);
        return;
      }

      if (autoFlipTargetPageRef.current === nextPage) {
        autoFlipTargetPageRef.current = null;
      }

      syncCurrentPage(nextPage);
    },
    [cancelPendingAutoProgression, canTurnPages, currentPage, syncCurrentPage],
  );

  const handleInit = useCallback(
    (event) => {
      const nextPage = event?.data?.page ?? initialPage;
      const nextOrientation = event?.data?.mode ?? "portrait";
      setOrientation(nextOrientation);
      syncCurrentPage(nextPage);
    },
    [initialPage, syncCurrentPage],
  );

  const handleOrientationChange = useCallback((event) => {
    setOrientation(event.data);
  }, []);

  const handleContinuePageAdvance = useCallback(() => {
    if (isCompletingCutScene) return;

    setIsCompletingCutScene(true);
    completionTimeoutRef.current = window.setTimeout(() => {
      onCutSceneComplete?.();

      if (!onCutSceneComplete) {
        setSelectedPanel(null);
        setElevatedPanelKey(null);
        setCurrentPage(0);
        flipBookRef.current?.pageFlip?.()?.turnToPage?.(0);
        setIsCompletingCutScene(false);
      }
    }, 500);
  }, [isCompletingCutScene, onCutSceneComplete]);

  const goToPreviousPage = useCallback(() => {
    if (!canTurnPages) return;
    if (currentPage <= 0) {
      return;
    }
    cancelPendingAutoProgression();
    flipBookRef.current?.pageFlip()?.flipPrev("top");
  }, [cancelPendingAutoProgression, canTurnPages, currentPage]);

  const goToNextPage = useCallback(() => {
    if (!canTurnPages) return;
    cancelPendingAutoProgression();
    if (canCompleteCutScene) {
      handleContinuePageAdvance();
      return;
    }
    if (currentPage >= lastBookPageIndex) return;
    flipBookRef.current?.pageFlip()?.flipNext("top");
  }, [
    cancelPendingAutoProgression,
    canCompleteCutScene,
    canTurnPages,
    currentPage,
    handleContinuePageAdvance,
    lastBookPageIndex,
  ]);

  useEffect(() => {
    if (!pauseBackgroundMusic) return undefined;

    AudioManager.pauseMusic?.();

    return () => {
      AudioManager.resumeMusic?.();
    };
  }, [pauseBackgroundMusic]);

  useEffect(() => {
    if (introTimeoutRef.current) {
      window.clearTimeout(introTimeoutRef.current);
      introTimeoutRef.current = null;
    }

    if (shouldSkipIntro) {
      setIsIntroPlaying(false);
      return undefined;
    }

    setIsIntroPlaying(true);
    introTimeoutRef.current = window.setTimeout(() => {
      setIsIntroPlaying(false);
      introTimeoutRef.current = null;
    }, CUT_SCENE_INTRO_LOCK_MS);

    return () => {
      if (introTimeoutRef.current) {
        window.clearTimeout(introTimeoutRef.current);
        introTimeoutRef.current = null;
      }
    };
  }, [shouldSkipIntro]);

  useEffect(() => {
    if (
      didScheduleCoverOpenRef.current ||
      !hasCover ||
      isIntroPlaying ||
      isCompletingCutScene ||
      selectedPanel ||
      currentPage !== 0 ||
      visiblePageIndices.length !== 1 ||
      bookPages[visiblePageIndices[0]]?.kind !== "cover"
    ) {
      return;
    }

    didScheduleCoverOpenRef.current = true;
    scheduleAutoProgression(() => {
      const {
        currentPage: latestCurrentPage,
        isCompletingCutScene: latestIsCompletingCutScene,
        isIntroPlaying: latestIsIntroPlaying,
        selectedPanel: latestSelectedPanel,
        visiblePageIndices: latestVisiblePageIndices = [],
        bookPages: latestBookPages = [],
      } = latestStateRef.current || {};

      if (
        latestCurrentPage !== 0 ||
        latestSelectedPanel ||
        latestIsIntroPlaying ||
        latestIsCompletingCutScene ||
        latestBookPages[latestVisiblePageIndices[0]]?.kind !== "cover"
      ) {
        return;
      }

      const nextSpreadStartPage = getNextSpreadStartPage();
      if (nextSpreadStartPage === null) {
        return;
      }

      autoFlipTargetPageRef.current = nextSpreadStartPage;
      flipBookRef.current?.pageFlip()?.flipNext("top");
    });
  }, [
    bookPages,
    currentPage,
    getNextSpreadStartPage,
    hasCover,
    isCompletingCutScene,
    isIntroPlaying,
    scheduleAutoProgression,
    selectedPanel,
    visiblePageIndices,
  ]);

  useEffect(() => {
    if (
      didScheduleFirstSpreadAdvanceRef.current ||
      !autoAdvanceFirstSpread ||
      isIntroPlaying ||
      isCompletingCutScene ||
      selectedPanel ||
      currentPage !== 0
    ) {
      return;
    }

    const nextSpreadStartPage = getNextSpreadStartPage();
    if (nextSpreadStartPage === null) {
      return;
    }

    didScheduleFirstSpreadAdvanceRef.current = true;
    scheduleAutoProgression(() => {
      const {
        currentPage: latestCurrentPage,
        isCompletingCutScene: latestIsCompletingCutScene,
        isIntroPlaying: latestIsIntroPlaying,
        selectedPanel: latestSelectedPanel,
      } = latestStateRef.current || {};

      if (
        latestCurrentPage !== 0 ||
        latestSelectedPanel ||
        latestIsIntroPlaying ||
        latestIsCompletingCutScene
      ) {
        return;
      }

      autoFlipTargetPageRef.current = nextSpreadStartPage;
      flipBookRef.current?.pageFlip()?.flipNext("top");
    });
  }, [
    autoAdvanceFirstSpread,
    currentPage,
    getNextSpreadStartPage,
    isCompletingCutScene,
    isIntroPlaying,
    scheduleAutoProgression,
    selectedPanel,
  ]);

  useEffect(() => {
    if (viewportSizeOverride) return undefined;

    const onResize = () => {
      setViewportSize(getViewportSize());
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [viewportSizeOverride]);

  useEffect(() => {
    if (!viewportSizeOverride) return;
    setViewportSize(viewportSizeOverride);
  }, [viewportSizeOverride]);

  useEffect(() => {
    const normalizedPage = clampPageIndex(
      currentPage,
      pageCount,
      orientation,
      usesLibraryCover,
    );
    if (normalizedPage === currentPage) return;

    setCurrentPage(normalizedPage);
    flipBookRef.current?.pageFlip?.()?.turnToPage?.(normalizedPage);
    onPageChange?.(normalizedPage);
  }, [currentPage, onPageChange, orientation, pageCount, usesLibraryCover]);

  useEffect(() => {
    const pendingAutoTurn = pendingAutoTurnTargetRef.current;
    if (
      !pendingAutoTurn ||
      pendingAutoTurn.page !== currentPage ||
      selectedPanel ||
      isIntroPlaying ||
      isCompletingCutScene
    ) {
      return;
    }

    pendingAutoTurnTargetRef.current = null;
    if (!pendingAutoTurn.shouldOpenPanel) {
      return;
    }

    scheduleAutoProgression(() => {
      const {
        isCompletingCutScene: latestIsCompletingCutScene,
        isIntroPlaying: latestIsIntroPlaying,
        selectedPanel: latestSelectedPanel,
        visiblePageIndices: latestVisiblePageIndices = [],
      } = latestStateRef.current || {};

      if (
        latestSelectedPanel ||
        latestIsIntroPlaying ||
        latestIsCompletingCutScene
      ) {
        return;
      }

      const nextPanel = getFirstIncompletePanel(latestVisiblePageIndices);
      if (nextPanel) {
        openPanelViewer(nextPanel);
      }
    }, CUT_SCENE_NEXT_PANEL_OPEN_DELAY_MS);
  }, [
    currentPage,
    getFirstIncompletePanel,
    isCompletingCutScene,
    isIntroPlaying,
    openPanelViewer,
    scheduleAutoProgression,
    selectedPanel,
  ]);

  useEffect(() => {
    return () => {
      if (introTimeoutRef.current) {
        window.clearTimeout(introTimeoutRef.current);
      }
      if (completionTimeoutRef.current) {
        window.clearTimeout(completionTimeoutRef.current);
      }
      if (autoProgressTimeoutRef.current) {
        window.clearTimeout(autoProgressTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    onProgressChange?.({
      currentPage,
      completedPanels: Object.keys(completedPanels).filter((panelKey) => completedPanels[panelKey]),
    });
  }, [completedPanels, currentPage, onProgressChange]);

  useEffect(() => {
    if (
      !isCurrentScopeComplete ||
      visiblePageIndices.length === 0 ||
      visiblePanels.length === 0
    ) {
      return;
    }

    const scopeKey = `${orientation}:${visiblePageIndices.join(",")}`;
    if (completedSpreadKeysRef.current.has(scopeKey)) {
      return;
    }

    completedSpreadKeysRef.current.add(scopeKey);
    onSpreadComplete?.(visiblePageIndices[0], {
      orientation,
      pageIndices: visiblePageIndices,
    });
  }, [
    isCurrentScopeComplete,
    onSpreadComplete,
    orientation,
    visiblePanels.length,
    visiblePageIndices,
  ]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (selectedPanel) {
          closePanelViewer({ markComplete: true, reason: "escape" });
        }
        return;
      }

      if (selectedPanel) {
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToPreviousPage();
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        goToNextPage();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    closePanelViewer,
    goToNextPage,
    goToPreviousPage,
    selectedPanel,
  ]);
  const selectedPanelScript = useMemo(
    () => (selectedPanel ? getExpandedScript(selectedPanel, Boolean(selectedPanel.videoUrl)) : []),
    [selectedPanel],
  );
  const selectedPanelThumbnail = selectedPanel
    ? getFrameThumbnail(selectedPanel)
    : null;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "grid",
        placeItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "grid",
          placeItems: "center",
          px: { xs: 1, md: 3 },
          py: { xs: 3, md: 4 },
          perspective: "1000px",
        }}
      >
        <Box
            sx={{
              position: "relative",
              width: bookWidth,
              height: bookHeight,
              maxWidth: "100%",
              maxHeight: "100%",
              display: "grid",
              placeItems: "center",
              transformStyle: "preserve-3d",
            }}
          >
          <Box
            component={motion.div}
            data-testid="cut-scene-book-shadow"
            aria-hidden
            initial="hidden"
            animate="visible"
            variants={shadowIntroVariants}
            transition={introTransition}
            sx={{
              position: "absolute",
              left: "7%",
              right: "7%",
              bottom: -14,
              height: 34,
              borderRadius: "50%",
              background:
                "radial-gradient(ellipse at center, rgba(4, 8, 19, 0.42) 0%, rgba(4, 8, 19, 0.24) 48%, rgba(4, 8, 19, 0.04) 76%, rgba(4, 8, 19, 0) 100%)",
              filter: "blur(12px)",
              transformOrigin: "50% 50%",
              pointerEvents: "none",
            }}
          />

          <Box
            component={motion.div}
            data-testid="cut-scene-book-stage"
            initial="hidden"
            animate="visible"
            variants={bookIntroVariants}
            transition={introTransition}
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              transformStyle: "preserve-3d",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transformOrigin: "50% 8%",
              willChange: "transform, opacity",
            }}
          >
            <AnimatePresence initial={false}>
              <Box
                key={`open-book-${orientation}-${pageWidth}-${pageHeight}`}
                sx={{
                  width: bookWidth,
                  height: bookHeight,
                  maxWidth: "100%",
                  maxHeight: "100%",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <HTMLFlipBook
                  ref={flipBookRef}
                  width={pageWidth}
                  height={pageHeight}
                  minWidth={pageWidth}
                  maxWidth={pageWidth}
                  minHeight={pageHeight}
                  maxHeight={pageHeight}
                  startPage={currentPage}
                  size="fixed"
                  drawShadow
                  maxShadowOpacity={0.35}
                  showCover={usesLibraryCover}
                  mobileScrollSupport={false}
                  usePortrait
                  flippingTime={CUT_SCENE_PAGE_TURN_DURATION_MS}
                  useMouseEvents={
                    !isIntroPlaying &&
                    !selectedPanel &&
                    (!enforcePanelSequence || isCurrentScopeComplete)
                  }
                  swipeDistance={24}
                  clickEventForward
                  onFlip={handleFlip}
                  onInit={handleInit}
                  onChangeOrientation={handleOrientationChange}
                  style={{
                    width: `${bookWidth}px`,
                    height: `${bookHeight}px`,
                    filter: "drop-shadow(0 30px 50px rgba(0, 0, 0, 0.45))",
                  }}
                >
                  {bookPages.map((page, index) => (
                    <ComicPage
                      key={`${page.id}-${index}`}
                      page={page}
                      pageIndex={index}
                      allowZoom={allowZoom && !isIntroPlaying}
                      onSelectPage={page.kind ? undefined : onPageSelect}
                      onSelectPanel={page.kind ? undefined : onPanelSelect}
                      showPageMeta={showPageMeta}
                      showPanelText={showPanelText}
                      enforcePanelSequence={enforcePanelSequence}
                      currentPanelKey={currentPanelKey}
                      elevatedPanelKey={elevatedPanelKey}
                      completedPanels={completedPanels}
                      isContinuePageLocked={
                        page.kind === "continue" &&
                        shouldAppendContinuePage &&
                        enforcePanelSequence &&
                        !isCurrentScopeComplete
                      }
                      onSelectFrame={handleSelectPanel}
                    />
                  ))}
                </HTMLFlipBook>
              </Box>
            </AnimatePresence>
          </Box>
        </Box>
      </Box>

      {showNav ? (
        <>
          <IconButton
            onClick={goToPreviousPage}
            disabled={
              !canGoBack
            }
            aria-label="Previous page"
            sx={{
              position: "absolute",
              left: { xs: 8, md: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "rgba(12, 18, 34, 0.8)",
              color: "#f8fafc",
              border: "1px solid rgba(255,255,255,0.14)",
              "&:hover": { bgcolor: "rgba(20, 29, 54, 0.92)" },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.25)",
                bgcolor: "rgba(12,18,34,0.38)",
              },
            }}
          >
            <ChevronLeftRoundedIcon />
          </IconButton>

          <IconButton
            onClick={goToNextPage}
            disabled={
              (!canCompleteCutScene && currentPage >= lastBookPageIndex) ||
              !canTurnPages
            }
            aria-label="Next page"
            sx={{
              position: "absolute",
              right: { xs: 8, md: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "rgba(12, 18, 34, 0.8)",
              color: "#f8fafc",
              border: "1px solid rgba(255,255,255,0.14)",
              "&:hover": { bgcolor: "rgba(20, 29, 54, 0.92)" },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.25)",
                bgcolor: "rgba(12,18,34,0.38)",
              },
            }}
          >
            <ChevronRightRoundedIcon />
          </IconButton>
        </>
      ) : null}

      {showStatus ? (
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            bottom: { xs: 10, md: 20 },
            transform: "translateX(-50%)",
            px: 2,
            py: 1,
            borderRadius: 999,
            bgcolor: "rgba(6, 10, 24, 0.72)",
            border: "1px solid rgba(255,255,255,0.12)",
            backdropFilter: "blur(8px)",
            textAlign: "center",
          }}
        >
          <Typography sx={{ color: "#f8fafc", fontSize: 12, fontWeight: 700 }}>
            {visiblePageLabel}
          </Typography>
          <Typography sx={{ color: "rgba(248,250,252,0.72)", fontSize: 11 }}>
            {canCompleteCutScene
              ? "Turn the final page to close the cut scene."
              : isIntroPlaying
              ? "The story is settling into place."
              : isCurrentScopeComplete
              ? "Drag to flip, or use the arrow keys."
              : "Finish the unlocked panels to turn the page."}
          </Typography>
        </Box>
      ) : null}

      <AnimatePresence>
        {isCompletingCutScene ? (
          <Box
            component={motion.div}
            data-testid="cut-scene-fade"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 80,
              bgcolor: "#000",
              pointerEvents: "none",
            }}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {selectedPanel ? (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => closePanelViewer({ markComplete: true, reason: "backdrop" })}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 50,
              bgcolor: "rgba(0,0,0,0.88)",
              display: "grid",
              placeItems: "center",
              cursor: "zoom-out",
              p: 2,
            }}
          >
            <Box
              component={motion.div}
              layoutId={selectedPanel.layoutId}
              onClick={(event) => event.stopPropagation()}
              sx={{
                position: "relative",
                width: "min(100vw, 1400px)",
                height: "min(100vh, 900px)",
                border: "3px solid #fff",
                overflow: "hidden",
                backgroundColor: "#000",
                boxShadow: "0 30px 80px rgba(0,0,0,0.55)",
              }}
            >
              {selectedPanel.videoUrl ? (
                <video
                  autoPlay
                  playsInline
                  poster={selectedPanelThumbnail || undefined}
                  onEnded={() =>
                    closePanelViewer({
                      markComplete: true,
                      reason: "video-ended",
                    })
                  }
                  aria-label={selectedPanel.alt || "expanded video panel"}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    backgroundColor: "#000",
                  }}
                >
                  <source src={selectedPanel.videoUrl} />
                </video>
              ) : (
                <img
                  src={selectedPanel.image}
                  alt={selectedPanel.alt || "expanded frame"}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}

              <IconButton
                onClick={() =>
                  closePanelViewer({ markComplete: true, reason: "close-button" })
                }
                aria-label="Close panel"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  zIndex: 3,
                  color: "#e2e8f0",
                  bgcolor: "rgba(15, 23, 42, 0.72)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  "&:hover": {
                    bgcolor: "rgba(30, 41, 59, 0.9)",
                  },
                }}
              >
                <CloseRoundedIcon />
              </IconButton>

              {!selectedPanel.videoUrl && selectedPanelScript.length > 0 ? (
                <DialogHUD
                  theme={theme}
                  open
                  script={selectedPanelScript}
                  onClose={finishSelectedPanel}
                  showInstant
                  backdrop={false}
                  captureBackdrop={false}
                />
              ) : null}
            </Box>
          </Box>
        ) : null}
      </AnimatePresence>
    </Box>
  );
}
